import { useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import { sendCampaignLaunchNotification } from '@/lib/slack-wrapper'

interface CampaignLaunchNotificationOptions {
  enabled?: boolean
  onNotificationSent?: (campaignId: string, success: boolean) => void
}

export function useCampaignLaunchNotifications(options: CampaignLaunchNotificationOptions = {}) {
  const { enabled = true, onNotificationSent } = options

  const getClientSlackChannel = useCallback(async (clientId: string): Promise<string | undefined> => {
    try {
      const { data: client, error } = await supabase
        .from('clients')
        .select('slack_main_channel, name')
        .eq('id', clientId)
        .single()

      if (error) {
        console.error('Error fetching client Slack channel:', error)
        return undefined
      }

      return client?.slack_main_channel
    } catch (error) {
      console.error('Error getting client Slack channel:', error)
      return undefined
    }
  }, [])

  const handleCampaignLaunch = useCallback(async (campaign: any) => {
    if (!enabled) return

    try {
      console.log('🚀 Campaign launch detected:', campaign)

      // Get client Slack channel
      const clientSlackChannel = await getClientSlackChannel(campaign.client_id)

      // Prepare campaign data for notification
      const campaignData = {
        id: campaign.id,
        name: campaign.name,
        client_id: campaign.client_id,
        client_name: campaign.client_name,
        status: campaign.status,
        campaign_type: campaign.campaign_type,
        version: campaign.version,
        target_regions: campaign.target_regions,
        channels: campaign.channels,
        description: campaign.description,
        total_leads: campaign.total_leads,
        created_at: campaign.created_at
      }

      // Send notification
      const success = await sendCampaignLaunchNotification(campaignData, clientSlackChannel)

      if (onNotificationSent) {
        onNotificationSent(campaign.id, success)
      }

      if (success) {
        console.log('✅ Campaign launch notification sent successfully')
      } else {
        console.error('❌ Failed to send campaign launch notification')
      }
    } catch (error) {
      console.error('💥 Error handling campaign launch notification:', error)
      if (onNotificationSent) {
        onNotificationSent(campaign.id, false)
      }
    }
  }, [enabled, getClientSlackChannel, onNotificationSent])

  useEffect(() => {
    if (!enabled) return

    console.log('🔔 Setting up campaign launch notifications...')

    // Listen for new campaigns being created (but only if they're created as active/live)
    const newCampaignChannel = supabase
      .channel('campaign_launch_notifications_new')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'campaigns'
        },
        (payload) => {
          console.log('🆕 New campaign detected for launch notification:', payload.new)
          // Only trigger if campaign is created with active/live status (shouldn't happen with draft default)
          if (payload.new?.status === 'active' || payload.new?.status === 'live') {
            console.log('⚠️ Campaign created with active/live status - this should not happen with draft default')
            handleCampaignLaunch(payload.new)
          }
        }
      )
      .subscribe()

    // Listen for campaign status changes to 'active' or 'live'
    const statusChangeChannel = supabase
      .channel('campaign_launch_notifications_status')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'campaigns'
        },
        (payload) => {
          const oldStatus = payload.old?.status
          const newStatus = payload.new?.status
          
          // Only trigger notification if status changed to active/live from draft or paused
          // This prevents duplicate notifications and ensures we only notify on actual launches
          if (
            oldStatus !== newStatus && 
            (newStatus === 'active' || newStatus === 'live') &&
            (oldStatus === 'draft' || oldStatus === 'paused')
          ) {
            console.log('🚀 Campaign launched - status changed from', oldStatus, 'to', newStatus, ':', payload.new)
            handleCampaignLaunch(payload.new)
          }
        }
      )
      .subscribe()

    return () => {
      console.log('🔕 Cleaning up campaign launch notifications...')
      newCampaignChannel.unsubscribe()
      statusChangeChannel.unsubscribe()
    }
  }, [enabled, handleCampaignLaunch])

  return {
    handleCampaignLaunch,
    getClientSlackChannel
  }
}
